import React from 'react';
import BlogPost from '../../../components/BlogPost';

const Observability = () => (
  <BlogPost
    breadcrumbItems={[
      { path: '/', label: 'Home' },
      { path: '/blog', label: 'Blog' },
      { path: '/blog/cloud-security-series', label: 'Cloud Security Series' },
      { path: '/blog/cloud-security-series/observability', label: 'Observability' },
    ]}
    title="Observability"
    date="Mar 3, 2025"
    content={
      <>
        <section className="blog-post__section">
          <h2>Observability</h2>
          <p>Cloud Infrastructure Security Series #10. 🔍 Observability</p>
          <p>
            In the original post we defined steps to implement a bullet-proof Cloud Infrastructure
            Security Posture. Now it's time to dive into the topic of{' '}
            <strong>🔍 Observability</strong>.
          </p>
          <p>
            <strong>❓ How would you approach the implementation of the following?</strong>
            <br />
            – Implement Centralized Logging (from cloud services and applications);
            <br />– Configure Alerts on Critical and High Impact VMS Events.
          </p>
          <p>
            <strong>💪 Here is how, in a few well-defined steps:</strong>
          </p>
          <ol>
            <li>
              <b>Option No.1: Managed setup.</b>
              <ul className="blog-post__indented-list">
                <li>
                  Leverage cloud service provider's SIEM, e.g. CloudWatch in AWS –{' '}
                  <a href="https://lnkd.in/ev6ssGz7" target="_blank" rel="noopener noreferrer">
                    link
                  </a>
                  , Cloud Operations Suite in GCP –{' '}
                  <a href="https://lnkd.in/eFTaRrQ7" target="_blank" rel="noopener noreferrer">
                    link
                  </a>
                  , Azure Monitor in MS Azure –{' '}
                  <a href="https://lnkd.in/eZsZAriE" target="_blank" rel="noopener noreferrer">
                    link
                  </a>
                  .
                </li>
                <li>
                  Stream all logs from all services and applications directly to the CSP-provided
                  Centralized Logging Service.
                </li>
              </ul>
            </li>
            <li>
              <b>Option No.2: Custom setup.</b>
              <ul className="blog-post__indented-list">
                <li>
                  (Optional, but provides a structured approach to log collection). Start with
                  creating a centralized cloud storage for all application and service logs. Create
                  separate folder per application/cloud service, and configure streaming of logs
                  into those folders.
                </li>
                <li>
                  Install SIEM solution in your cloud environment. For example, ElasticSearch (
                  <a href="https://lnkd.in/e-3yvV33" target="_blank" rel="noopener noreferrer">
                    link
                  </a>
                  ) or OpenSearch (
                  <a href="https://opensearch.org/" target="_blank" rel="noopener noreferrer">
                    link
                  </a>
                  ) are widely popular.
                </li>
                <li>
                  Configure streaming of all log records from different folders in centralized cloud
                  storage (or directly from applications and services) into the corresponding
                  indexes in SIEM.
                </li>
              </ul>
            </li>
            <li>
              In your SIEM of choice (Option No.1, or No.2), take time to review the incoming log
              records, and for each type of service/application define a set of filters that will
              help you detect application/service errors or concerning trends.
              <ul className="blog-post__indented-list">
                <li>Build Dashboards to visualize your filters.</li>
                <li>For critical errors, set up email alerts.</li>
                <li>
                  Deploy Alarms or Monitors that help you detect anomalies in the log records, as
                  well as their corresponding Alerts.
                </li>
                <li>
                  Test your Dashboards and Alerts to make sure they provide enough data reference
                  points and good insights into data in case of security or operational incidents.
                </li>
              </ul>
            </li>
            <li>
              Review the Dashboards provided by your XDR (e.g. Wazuh, for example). Over time,
              fine-tune filters to reduce the number of incoming security events.
            </li>
            <li>Configure email Alerts in your XDR on High and Critical impact events.</li>
            <li>Make rule to visit your SIEM and XDR Dashboards at least 2-3 times a week.</li>
            <li>
              Over time, reduce the number of incoming email Alerts to the most significant ones.
            </li>
          </ol>
          <p>
            Be pro-active with data, filter out noise, prioritize the most impactful events, to
            achieve the best observability and incident management results.
          </p>
        </section>
      </>
    }
    cta={{
      title: 'We Are Here to Help!',
      text: "Struggling to set up or simplify your SOC 2 compliance? Our expert team at DevSecOps, Inc. specializes in making compliance manageable and straightforward for lean startups. We've successfully helped numerous startups quickly and affordably achieve compliance without sacrificing agility.",
      buttonText: `Let's Talk`,
      buttonHref: '/contact-us',
    }}
    prevPost={{
      href: '/blog/cloud-security-series/antivirus-scanning-and-security-patching',
      label: 'Antivirus Scanning and Security Patching',
    }}
  />
);

export default Observability;
